import axios, { AxiosError, AxiosResponse } from 'axios'
import axiosRetry from 'axios-retry'
import {
    AttachPaymentMethodRequest,
    CancelSubscriptionRequest,
    Coupon,
    CreateAccessCodeRequest,
    CreateAccessCodeResponse,
    CreateApiKeyRequest,
    CreateApiKeyResponse,
    CreateCompanyRequest,
    CreateCompanyResponse,
    CreateCouponRequest,
    CreateSubscriptionRequest,
    CreateUserRequest,
    CreateUserResponse,
    DeleteAccessCodeRequest,
    DeleteCouponRequest,
    DeleteUserRequest,
    EditAccessCodeRequest,
    EditApiKeyRequest,
    EditCompanyRequest,
    EditSubscriptionQuantityRequest,
    EditUserRequest,
    ManageUserSubscriptionRequest,
    PaymentMethod,
    QueryApiEventJobCountsRequest,
    QueryApiEventJobCountsResponse,
    QueryCouponsRequest,
    QueryCouponsResponse,
    QueryEquipmentRequest,
    QueryEquipmentResponse,
    QueryInvoicesRequest,
    QueryInvoicesResponse,
    QueryPaymentMethodsRequest,
    QueryPaymentMethodsResponse,
    QuerySubscriptionsRequest,
    QuerySubscriptionsResponse,
    SetDefaultPaymentMethodRequest,
    Subscription,
    TaxRatesResponse
} from '@/core/ApiTypes'
import appManager from '@/services/AppManager'

const baseUrl = process.env.VUE_APP_API_BASE_URL

axiosRetry(axios, { retries: 3, retryDelay: axiosRetry.exponentialDelay})

export async function createCompany(request: CreateCompanyRequest): Promise<CreateCompanyResponse> {
    const response = await axios.post(`${baseUrl}/companies/create`, request)
    return response.data
}

export async function editCompany(request: EditCompanyRequest): Promise<void> {
    await postWithAuth(`${baseUrl}/companies/edit_profile`, request)
}

export async function createUser(request: CreateUserRequest): Promise<CreateUserResponse> {
    const response = await postWithAuth(`${baseUrl}/users/create`, request)
    return response.data
}

export async function editUser(request: EditUserRequest): Promise<void> {
    await postWithAuth(`${baseUrl}/users/edit_profile`, request)
}

export async function deleteUser(request: DeleteUserRequest): Promise<void> {
    await postWithAuth(`${baseUrl}/users/delete`, request)
}

export async function manageUserSubscription(request: ManageUserSubscriptionRequest): Promise<void> {
    await postWithAuth(`${baseUrl}/users/manage_subscription`, request)
}

export async function createAccessCode(request: CreateAccessCodeRequest): Promise<CreateAccessCodeResponse> {
    const response = await postWithAuth(`${baseUrl}/access_codes/create`, request)
    return response.data
}

export async function editAccessCode(request: EditAccessCodeRequest): Promise<void> {
    await postWithAuth(`${baseUrl}/access_codes/edit`, request)
}

export async function deleteAccessCode(request: DeleteAccessCodeRequest): Promise<void> {
    await postWithAuth(`${baseUrl}/access_codes/delete`, request)
}

export async function getSubscription(stripeSubscriptionId: string): Promise<Subscription> {
    const response = await getWithAuth(`${baseUrl}/subscriptions/${stripeSubscriptionId}`)
    return response.data
}

export async function querySubscriptions(request: QuerySubscriptionsRequest): Promise<QuerySubscriptionsResponse> {
    const response = await postWithAuth(`${baseUrl}/subscriptions/query`, request)
    return response.data
}

export async function createSubscription(request: CreateSubscriptionRequest): Promise<Subscription> {
    const response = await postWithAuth(`${baseUrl}/subscriptions/create`, request)
    return response.data
}

export async function editSubscriptionQuantity(request: EditSubscriptionQuantityRequest): Promise<Subscription> {
    const response = await postWithAuth(`${baseUrl}/subscriptions/edit_quantity`, request)
    return response.data
}

export async function cancelSubscription(request: CancelSubscriptionRequest): Promise<Subscription> {
    const response = await postWithAuth(`${baseUrl}/subscriptions/cancel`, request)
    return response.data
}

export async function getCoupon(stripeCouponId: string): Promise<Coupon> {
    const response = await getWithAuth(`${baseUrl}/coupons/${stripeCouponId}`)
    return response.data
}

export async function queryCoupons(request: QueryCouponsRequest): Promise<QueryCouponsResponse> {
    const response = await postWithAuth(`${baseUrl}/coupons/query`, request)
    return response.data
}

export async function createCoupon(request: CreateCouponRequest): Promise<Coupon> {
    const response = await postWithAuth(`${baseUrl}/coupons/create`, request)
    return response.data
}

export async function deleteCoupon(request: DeleteCouponRequest): Promise<void> {
    await postWithAuth(`${baseUrl}/coupons/delete`, request)
}

export async function queryEquipment(request: QueryEquipmentRequest): Promise<QueryEquipmentResponse> {
    const response = await postWithAuth(`${baseUrl}/equipment/query`, request)
    return response.data
}

export async function queryInvoices(request: QueryInvoicesRequest): Promise<QueryInvoicesResponse> {
    const response = await postWithAuth(`${baseUrl}/invoices/query`, request)
    return response.data
}

export async function queryPaymentMethods(request: QueryPaymentMethodsRequest): Promise<QueryPaymentMethodsResponse> {
    const response = await postWithAuth(`${baseUrl}/payment_methods/query`, request)
    return response.data
}

export async function getDefaultPaymentMethod(companyId: string): Promise<PaymentMethod|null> {
    const response = await getWithAuth(`${baseUrl}/payment_methods/default/${companyId}`)

    // an empty object will be returned if there is no default payment metho
    const paymentMethod: PaymentMethod = response.data
    return (!paymentMethod?.id) ? null : paymentMethod
}

export async function setDefaultPaymentMethod(request: SetDefaultPaymentMethodRequest): Promise<void> {
    await postWithAuth(`${baseUrl}/payment_methods/default`, request)
}

export async function attachPaymentMethod(request: AttachPaymentMethodRequest): Promise<void> {
    await postWithAuth(`${baseUrl}/payment_methods/attach`, request)
}

export async function getTaxRatesByZipCode(zipCode: string): Promise<TaxRatesResponse> {
    const response = await getWithAuth(`${baseUrl}/taxes/zip_code/${zipCode}`)
    return response.data
}

export async function queryApiEventJobCounts(request: QueryApiEventJobCountsRequest): Promise<QueryApiEventJobCountsResponse> {
    const response = await postWithAuth(`${baseUrl}/api/query-job-counts`, request)
    return response.data
}

export async function createApiKey(request: CreateApiKeyRequest): Promise<CreateApiKeyResponse> {
    const response = await postWithAuth(`${baseUrl}/api/create-key`, request)
    return response.data
}

export async function editApiKey(request: EditApiKeyRequest): Promise<void> {
    const response = await postWithAuth(`${baseUrl}/api/edit-key`, request)
    return response.data
}

export function extractResponseStatus(error: AxiosError|any): number|undefined {
    return error.response?.status
}

export function extractResponseErrorMessage(error: AxiosError|any): string|undefined {
    return error.response?.data?.message || error?.message
}

async function getWithAuth(url: string): Promise<AxiosResponse> {
    const token = await appManager.userManager.createUserToken()
    return await axios.get(url, {
        headers: {
            Authorization: 'Bearer ' + token
        }
    })
}

async function postWithAuth(url: string, data?: any): Promise<AxiosResponse> {
    const token = await appManager.userManager.createUserToken()
    return await axios.post(url, data, {
        headers: {
            Authorization: 'Bearer ' + token
        }
    })
}